import { Box, Button, Card, CardBody, CardFooter, CardHeader, Image, Layer, List, Text, Tip } from "grommet"
import { useDispatch, useSelector } from "react-redux"
import { authenticationSelector } from "../../slices/authSlice"
import { TwitchAuth } from "../auth/TwitchAuth"
import { DraggableDonateCard } from "./DraggableDonateCard"
import { Add, CircleInformation, Close, Trash } from "grommet-icons"
import { Trans, useTranslation } from "react-i18next"
import { formattedPriceNumber } from "../../utils/priceUtils"
import { addLot, mergeAuctionData } from "../../slices/auctionDataSlice"
import { useRemoveAuctionDonateRecordsMutation } from "../../api/donateRecordsApi"
import { setDonates } from "../../slices/donatesSlice"
import { useState } from "react"

export const DonatesList = ({ data }) => {

    const { t, ready } = useTranslation("donate-card", { useSuspense: false });

    const isAuthenticated = useSelector(authenticationSelector);

    const [removeAuctionDonateRecords] = useRemoveAuctionDonateRecordsMutation();

    const dispatch = useDispatch();

    const [showAddAllConfirmationDialog, setShowAddAllConfirmationDialog] = useState(false);
    const [showRemoveAllConfirmationDialog, setShowRemoveAllConfirmationDialog] = useState(false);

    const handleAddAll = () => {
        if (data.length > 0) {
            data.map(row => ({ lotName: `${row.message}${row.appendUsername ? ` (${row.sender})` : ''}`, cost: formattedPriceNumber(row.currencyAmount) }))
                .forEach(row => dispatch(addLot(row)))
            removeAuctionDonateRecords(data.map(row => row.id)).unwrap()
                .then(_ => dispatch(setDonates([])));
        }
        setShowAddAllConfirmationDialog(false);
    }

    const handleAddAllMerge = () => {
        if (data.length > 0) {
            const lots = data.map(row => ({ lotName: `${row.message}${row.appendUsername ? ` (${row.sender})` : ''}`, cost: formattedPriceNumber(row.currencyAmount) }));
            dispatch(mergeAuctionData(lots));
            removeAuctionDonateRecords(data.map(row => row.id)).unwrap()
                .then(_ => dispatch(setDonates([])));
        }
        setShowAddAllConfirmationDialog(false);
    }

    const handleRemoveAll = () => {
        if (data.length > 0) {
            removeAuctionDonateRecords(data.map(row => row.id)).unwrap()
                .then(_ => dispatch(setDonates([])));
        }
        setShowRemoveAllConfirmationDialog(false);
    }

    const addAllDialogData = {
        header: 'addAllDialogHeader',
        bodyText: 'addAllDialogBodyText',
        actions: [
            {
                label: 'addNewActionLabel',
                onClick: handleAddAll,
                primary: true
            },
            {
                label: 'mergeActionLabel',
                onClick: handleAddAllMerge,
                primary: true
            }
        ]
    }

    const removeAllDialogData = {
        header: 'removeAllDialogHeader',
        bodyText: 'removeAllDialogBodyText',
        actions: [
            {
                label: 'cancelActionLabel',
                onClick: () => setShowRemoveAllConfirmationDialog(false),
                primary: false
            },
            {
                label: 'confirmActionLabel',
                onClick: handleRemoveAll,
                primary: true
            }
        ]
    }

    return (
        <Box>
            {isAuthenticated && <Box>
                <Card margin={{ bottom: 'medium' }}>
                    <CardBody gap="small">
                        <Button label={t('addAllDonatesLabel')} secondary icon={<Add />} size="small" onClick={() => setShowAddAllConfirmationDialog(true)} />
                        <Button label={t('removeAllDonatesLabel')} secondary icon={<Trash />} size="small" onClick={() => setShowRemoveAllConfirmationDialog(true)} />
                    </CardBody>
                </Card>
                {data?.length > 0 && (
                    <List
                        data={data}
                        primaryKey="id"
                        pad={'none'}
                        children={(item, index) => (
                            <DraggableDonateCard item={item} index={index} />
                        )}
                    />
                )}
                {data?.length === 0 && (
                    <Box border={{ size: '1px', style: 'dashed', color: 'brand' }} round="small" flex="grow" align="center" justify="center" pad={'medium'}>
                        <Tip content={<Box round="small" pad={{ top: 'small', bottom: 'small' }}><Image height={'400px'} width={'800px'} src={`${process.env.REACT_APP_CDN_BASE_URL}/auction/donate_dragg_preview.gif`} /></Box>} dropProps={{ align: { bottom: 'top' } }}><CircleInformation /></Tip>
                        <Text textAlign="center" size="small"><Trans t={t} i18nKey={'donatesListHelp'} components={{ SettingsLink: <Button plain href='/auction/settings' label={t('hereLabel')} style={{ textDecoration: 'underline' }} />, DonationLink: <Button plain href='/settings/donate-options' label={t('hereLabel')} target="_blank" style={{ textDecoration: 'underline' }} /> }} /></Text>
                    </Box>
                )}
            </Box>}
            {!isAuthenticated && (
                <Card background="#222222">
                    <CardBody pad="medium">
                        <Box justify="center" align="center" gap="medium">
                            <Text textAlign="center"><Trans t={t} i18nKey={'signInForDonatesTitle'} /></Text>
                            <TwitchAuth />
                        </Box>
                    </CardBody>
                </Card>
            )}
            {showAddAllConfirmationDialog && <ConfirmationDialog data={addAllDialogData} onClose={() => setShowAddAllConfirmationDialog(false)} />}
            {showRemoveAllConfirmationDialog && <ConfirmationDialog data={removeAllDialogData} onClose={() => setShowRemoveAllConfirmationDialog(false)} />}
        </Box>
    )
}

const ConfirmationDialog = ({ data, onClose }) => {

    const { t, ready } = useTranslation("donate-card", { useSuspense: false });

    if (ready) {
        return (
            <Layer onClickOutside={onClose}>
                <Card>
                    <CardHeader align="center">
                        <Text weight={'bold'}>{t(data.header)}</Text>
                        <Button icon={<Close />} onClick={onClose} />
                    </CardHeader>
                    <CardBody pad={{ top: 'none', bottom: 'none', left: 'medium', right: 'medium' }}>{<Trans t={t} i18nKey={data.bodyText} />}</CardBody>
                    <CardFooter pad="medium">
                        {data.actions.map((action, index) => <Button key={`confirm_action_${index}`} label={t(action.label)} primary={action.primary} onClick={action.onClick} />)}
                    </CardFooter>
                </Card>
            </Layer>
        )
    }
}